import React, { useEffect, useState } from "react";

import { isMessageKey } from "common/i18n/types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Header } from "../components/layout/header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { config } from "../config";
import { useTranslations } from "../hooks/use-translations";

import type { MessageKey } from "common/i18n/message-key";

export const ActionsPage = (): React.JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(``);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false);
  const [redirectInfo, setRedirectInfo] = useState<{ timeout: number; url: string } | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [progress, setProgress] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { translations } = useTranslations();

  const pageTitle = searchParams.get(`title`) ?? translations.actions.defaultTitle;

  useEffect(() => {
    const processAction = async () => {
      if (hasProcessed) return;

      setIsLoading(true);
      setHasProcessed(true);

      try {
        const actionPath = location.pathname.replace(`/actions`, ``);
        const queryString = location.search;
        const fullPath = `${actionPath}${queryString}`;

        const response = await fetch(`${config.apiBaseUrl}${fullPath}`, {
          method: `GET`,
          headers: {
            "Content-Type": `application/json`,
          },
        });

        const result = (await response.json()) as {
          success: boolean;
          message: MessageKey;
          redirectTimeout?: number;
          redirectUrl?: string;
        };

        if (result.success) {
          setIsSuccess(true);

          const translated = translations.auth[result.message] ?? translations.actions.success;

          setMessage(translated);

          if (result.redirectTimeout && result.redirectUrl) {
            setRedirectInfo({
              timeout: result.redirectTimeout,
              url: result.redirectUrl,
            });

            setCountdown(Math.ceil(result.redirectTimeout / 1_000));
          }
        } else {
          setIsSuccess(false);

          const translated = translations.auth[result.message] ?? translations.actions.error;

          setMessage(translated);
        }
      } catch (error) {
        setIsSuccess(false);

        if (error instanceof Error) {
          const rawMessage = error.message;

          if (isMessageKey(rawMessage)) {
            setMessage(translations.auth[rawMessage]);
          } else {
            const errorMessage = rawMessage.toLowerCase();

            if (errorMessage.includes(`server`) || errorMessage.includes(`internal`)) {
              setMessage(translations.actions.serverUnavailable);
            } else if (errorMessage.includes(`network`) || errorMessage.includes(`fetch`)) {
              setMessage(translations.actions.networkUnavailable);
            } else {
              setMessage(rawMessage);
            }
          }
        } else {
          setMessage(translations.actions.unexpectedError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    processAction();
  }, [location.pathname, location.search, hasProcessed, translations]);

  useEffect(() => {
    if (!redirectInfo || countdown <= 0) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        const newCount = prev - 1;
        const progressPercent = ((redirectInfo.timeout / 1_000 - newCount) / (redirectInfo.timeout / 1_000)) * 100;

        setProgress(progressPercent);

        if (newCount <= 0) {
          navigate(redirectInfo.url);
          return 0;
        }

        return newCount;
      });
    }, 1_000);

    return () => {
      clearInterval(interval);
    };
  }, [redirectInfo, countdown, navigate]);

  const handleManualRedirect = () => {
    if (redirectInfo) {
      navigate(redirectInfo.url);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="flex flex-1 items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card data-testid="actions-card">
            <CardHeader>
              <CardTitle className="text-center">
                {isLoading ? translations.actions.processingTitle : pageTitle}
              </CardTitle>
              <CardDescription className="text-center">
                {isLoading ? translations.actions.processingDescription : ``}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100" />
                </div>
              ) : null}

              {!isLoading && message ? (
                <div className="space-y-4">
                  <div
                    data-testid={isSuccess ? `actions-success` : `actions-error`}
                    className={`text-sm p-3 rounded-md text-center ${
                      isSuccess
                        ? `text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20`
                        : `text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20`
                    }`}
                  >
                    {message}
                  </div>

                  {redirectInfo && countdown > 0 ? (
                    <div className="space-y-3">
                      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                        {translations.actions.redirect.replace(`{count}`, countdown.toString())}
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-1000 ease-linear"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <Button className="w-full" onClick={handleManualRedirect}>
                        {translations.actions.continueNow}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
